import { max } from 'd3-array';
import { scalePow } from 'd3-scale';
import { forceSimulation, forceX, forceY, forceManyBody, forceCollide } from 'd3-force';
import { color } from 'd3-color';
import { quadtree } from 'd3-quadtree';
import shifty from 'shifty';
import _random from 'lodash/random';
import _groupBy from 'lodash/groupBy';
import _each from 'lodash/each';
import _find from 'lodash/find';
import _findIndex from 'lodash/findIndex';
import _clamp from 'lodash/clamp';

// Utils
import { minRadius, maxRadius, getDomain, dist, groupTitleLeft, groupTitleRigth, wrapLabel, labelMinRadius, separateTitleTextOpacity } from 'utils/chart';
import { regionColors, yearCanvasColor, separateTitleColor } from 'utils/colors';

export default class BubbleGraph {
  constructor(canvas) {
    this.context = canvas.node().getContext('2d');

    const options = {
      alphaTarget: 0.20,
      velocityDecay: 0.8,
    };

    this.simulation = forceSimulation()
      .velocityDecay(options.velocityDecay)
      .alphaTarget(options.alphaTarget)
      .alpha(0.4)
      .force('x', forceX(d => {
        let res = 0;
        if (this.options && this.options.isSeparate) {
          if (d.category === 'world_class') {
            res = (this.isWait ? 1 : -1) * (this.options.width / this.options.k) / 8;
          } else if (d.category === 'not_world_class') {
            res = (this.options.width / this.options.k) / 8;
          }
        }
        return res;
      }).strength(() => {
        return this.options && this.options.width / this.options.height > 1 ? 1.6 : 2;
      }))
      .force('y', forceY(120).strength(() => {
        return this.options && this.options.width / this.options.height < 1 ? 1.6 : 2;
      }))
      .force('charge', forceManyBody().strength(d => -Math.pow(d.radius, 1.5) * 12))
      // .force('collide', forceCollide().radius(d => d.radius).strength(0.99))
      // .force('collide', this._collide.bind(this))
      .force('cluster', this._cluster.bind(this, 0.03))
      .on('tick', this._tick.bind(this));

    this.radius = scalePow().exponent(0.5);
  }

  render(options, firstRender) {
    const { separate, data, context } = this;
    const { width, height, pixelRatio, isSeparate, currentYear, svgHeight } = options;
    this.options = options;
    if (isSeparate) {
      this.isWait = true;
      setTimeout(() => {
        this.isWait = false;
        this.simulation.alpha(0.1).nodes(this.nodes).restart();
      }, 2000);
    }
    this.bubbleData = (isSeparate && separate.map(d => d[currentYear]).filter(d => d).length) ? separate : data;

    this.radius.domain(getDomain(this.bubbleData, isSeparate ? '2025' : undefined));
    this._processData();

    context.translate((width * pixelRatio) / 2, ((height - svgHeight * 0.8) * pixelRatio) / 2 );
    context.scale(pixelRatio / options.k, pixelRatio / options.k);

    const tweenable = new shifty.Tweenable();
    const animateYear = duration => tweenable.tween({
      from: { opacity: 0 },
      to: { opacity: 0.4 },
      duration,
      step: state => {
        this.year = options.currentYear;
        this.textYearOpacity = state.opacity;
      },
    });

    if (firstRender) {
      this.simulation
        .nodes(this.nodes);
      this.filteredNodes.forEach(d => {
        d.x = Math.pow(-1, _random(0, 1)) * _random(width, width * 5);// _random(-width * 2, width * 2);
        d.y = _random(height / 4, height / 2);
        // d.y = _random(height * pixelRatio * 0.1, height * pixelRatio * 2);
      });

      animateYear(options.animationDelta);
    } else {
      this.simulation
        .alpha(0.1)
        .nodes(this.nodes)
        .restart();

      const radiusRange = this.radius.range();
      this.filteredNodes.forEach(d => {
        if (d.status === 'new') {
          const cluster = this.clusters ? this.clusters[`${d.region}-${d.category}`] : undefined;
          if (cluster && cluster !== d) {
            const angle = Math.random() * 2 * Math.PI;
            d.x = cluster.x + radiusRange[1] * Math.cos(angle);
            d.y = cluster.y + radiusRange[1] * Math.sin(angle);
          }
        }
      });

      tweenable.tween({
        from: { opacity: 0.4 },
        to: { opacity: 0.2 },
        duration: options.animationDelta / 8,
        step: state => {
          this.textYearOpacity = state.opacity;
        },
      }).then(() => animateYear(options.animationDelta * 3 / 4));
    }

    const sequenceDelta = options.animationDelta / this.filteredNodes.length;

    this.filteredNodes.forEach((node, i) => {
      const tweenable = new shifty.Tweenable();
      tweenable.tween({
        from: { radius: node.previousRadius },
        to: { radius: node.radius },
        duration: options.animationDelta - sequenceDelta * i,
        delay: sequenceDelta * i,
        step: state => {
          node.radius = state.radius;
        },
      });
    });

    if (this.dyingNodes.length) {
      this.dyingNodes.forEach(d => d.r = d.previousRadius || d.radius);
      const tweenable = new shifty.Tweenable();
      tweenable.tween({
        from: { t: 1 },
        to: { t: 0 },
        duration: options.animationDelta,
        step: state => {
          this.dyingNodes.forEach(d => {
            d.radius = state.t * d.r;
            if (d.radius === 0) d.dying = false;
          });
        },
      });
    }
  }

  _tick() {
    const { options, context, year, fontFamily } = this;
    if (options && this.filteredNodes) {
      const { svgHeight, width, height, k } = options;
      const w = width * k;
      const h = (height - svgHeight * 0.8) * k;
      const delta = h * 0.1;
      context.clearRect(-w / 2, -h / 2, w, h + svgHeight * k);
      context.save();

      // Draw year text
      context.font = `220pt ${fontFamily}`;
      context.globalAlpha = this.textYearOpacity;
      context.fillStyle = yearCanvasColor;
      context.fillText(year, -context.measureText(options.currentYear).width / 2, parseInt(context.font) / 2 + delta);

      // Draw links if Separate Mode
      if (this.options && this.options.isSeparate && this.hoveredNode) {
        const hoveredNodes = this.nodes.filter(d => d.name === this.hoveredNode.name);
        if (hoveredNodes.length === 2 && hoveredNodes[0].x && hoveredNodes[1].x) {
          context.globalAlpha = 1;
          context.beginPath();
          context.strokeStyle = regionColors[hoveredNodes[0].region];
          context.moveTo(hoveredNodes[0].x, hoveredNodes[0].y);
          context.lineTo(hoveredNodes[1].x, hoveredNodes[1].y);
          context.stroke();
        }
      }

      // Draw Nodes
      context.globalAlpha = 1;
      if (this.dyingNodes && this.dyingNodes.length) {
        this.dyingNodes.forEach(d => {
          if (d.dying) this._drawNode(d);
        });
      }
      this.filteredNodes.forEach(d => this._drawNode(d));

      // Draw annotation group text if Separate Mode
      context.globalAlpha = separateTitleTextOpacity;
      if (this.options && this.options.isSeparate) {
        context.font = `20pt ${fontFamily}`;
        context.fillStyle = separateTitleColor;
        const y = -h / 2 + parseInt(context.font) + 50;
        context.fillText(groupTitleLeft, -w / 4 - context.measureText(groupTitleLeft).width / 2, y + delta);
        context.fillText(groupTitleRigth, w / 4 - context.measureText(groupTitleRigth).width / 2, y + delta);
      }

      context.restore();
    }
  }

  _drawNode(d) {
    const { context, fontFamily } = this;
    context.beginPath();
    const x = d.x + (d.dx || 0);
    const y = d.y + (d.dy || 0);
    context.moveTo(x, y);
    context.arc(x, y, d.radius, 0, 2 * Math.PI);

    if (d.radius > 5) {
      context.strokeStyle = color(regionColors[d.region]).darker();
      context.stroke();
    }
    context.fillStyle = (this.hoveredNode && d.name === this.hoveredNode.name) ? color(regionColors[d.region]).brighter(0.6) : regionColors[d.region];
    context.fill();

    if (d.radius > 12) {
      context.font = `10pt ${fontFamily}`;
      const label = wrapLabel(d.name, context, 1.8 * d.radius);
      let textWidth = max(label.map(d => { return context.measureText(d).width; }));// context.measureText(d.name).width;
      context.fillStyle = 'white';
      let dy;
      if (textWidth > 1.8 * d.radius) {
        context.font = `6pt ${fontFamily}`;
        textWidth = max(label.map(d => { return context.measureText(d).width; }));
        if (textWidth > 1.8 * d.radius) {
          context.font = `4pt ${fontFamily}`;
          textWidth = max(label.map(d => { return context.measureText(d).width; }));
          if (textWidth > 1.8 * d.radius) {
            context.font = `4pt ${fontFamily}`;
          } else dy = 1.2;
        } else dy = 1.5;
      } else dy = 2;

      if (dy !== undefined && label.length * parseInt(context.font) < d.radius * 1.7) {
        label.forEach((d, i) => {
          textWidth = context.measureText(d).width;
          const dh = parseInt(context.font) * (label.length / 2 - 1 - i);
          context.fillText(d, x - textWidth / 2, y  - dy - dh);
        });
      }
    }
  }

  findNode(pos) {
    if (this.options) {
      const { width, height, svgHeight, k } = this.options;
      const mouseX = pos[0] - width / 2;
      const mouseY = pos[1] - (height - svgHeight * 0.8) / 2;
      let n;

      for (let i = 1; i < this.filteredNodes.length + 1; i++) {
        const node = this.filteredNodes[this.filteredNodes.length - i];
        if ( dist(node.x, node.y, mouseX * k, mouseY * k) < node.radius * k ) {
          n = node;
          break;
        }
      }
      this.hoveredNode = n;
      return n;
    }
  }

  _collide(alpha) {
    const padding = 8;
    const clusterPadding = 10;
    const qtree = quadtree()
      .x(d => d.x)
      .y(d => d.y)
      .addAll(this.nodes);
    this.nodes.forEach(d => {
      const r = d.radius + maxRadius + Math.max(padding, clusterPadding);
      const nx1 = d.x - r;
      const nx2 = d.x + r;
      const ny1 = d.y - r;
      const ny2 = d.y + r;
      qtree.visit((quad, x1, y1, x2, y2) => {
        if (quad.data && (quad.data !== d)) {
          let x = d.x - quad.data.x;
          let y = d.y - quad.data.y;
          let l = Math.sqrt(x * x + y * y);
          const r = d.radius + quad.data.radius + (d.region === quad.data.region ? padding : clusterPadding);
          if (l < r) {
            l = (l - r) / l * alpha;
            d.x -= x *= l;
            d.y -= y *= l;
            quad.data.x += x;
            quad.data.y += y;
          }
        }
        return x1 > nx2 || x2 < nx1 || y1 > ny2 || y2 < ny1;
      });
    });
  }

  _cluster(alpha) {
    const { clusters } = this;
    const grouped = _groupBy(this.filteredNodes, 'category');
    _each(grouped, group => {
      group.forEach(d => {
        const cluster = clusters ? clusters[`${d.region}-${d.category}`] : undefined;
        if (cluster && cluster !== d) {
          let x = d.x - cluster.x;
          let y = d.y - cluster.y;
          let l = Math.sqrt(x * x + y * y);
          const r = d.radius + cluster.radius;
          if (l > r) {
            l = (l - r) / l * alpha;
            d.x -= x *= l;
            d.y -= y *= l;
            cluster.x += _clamp(x, -0.05, 0.05);
            cluster.y += _clamp(y, -0.05, 0.05);
          }
        };
      });
    });
  }

  _processData() {
    const { options, bubbleData, radius } = this;
    this.clusters = {};

    this.nodes = bubbleData.map(d => {
      if (!this.nodes) {
        d.previousRadius = 0;
      } else {
        let findNode = _find(this.nodes, ['name', d.name]);
        if (findNode && findNode.category === 'world_class') {
          findNode = _find(this.nodes, { name: d.name, category: 'not_world_class' });
        }
        d.previousRadius = (findNode && findNode.radius && d.category !== 'world_class') ? findNode.radius : 0;
        if (findNode && findNode.x) {
          d.x = findNode.x;
          d.y = findNode.y;
        }

        const findInPreviousFilteredNode = _find(this.filteredNodes, ['name', d.name]);
        if (findInPreviousFilteredNode) d.status = 'old';
      }
      d.radius = +d[options.currentYear] ? radius(+d[options.currentYear]) : 0;

      if (!this.clusters[`${d.region}-${d.category}`] || (d.radius > this.clusters[`${d.region}-${d.category}`].radius)) this.clusters[`${d.region}-${d.category}`] = d;
      return d;
    });

    if (this.filteredNodes) this.filteredNodes.forEach(d => d.dying = true);

    const filteredNodes = this.nodes.filter(d => +d[options.currentYear]);
    filteredNodes.forEach(d => d.dying = false);

    if (!options.isSeparate) {
      if (this.filteredNodes) {
        if (this.filteredNodes[0].category !== 'all') {
          this.dyingNodes = this.filteredNodes.filter(d => (_findIndex(filteredNodes, ['name',  d.name ]) !== -1) && (d.category === 'world_class'));
        } else {
          this.dyingNodes = this.filteredNodes ? this.filteredNodes.filter(d => d.dying === true) : [];
        }
      } else {
        this.dyingNodes = [];
      }
    } else {
      this.dyingNodes = this.filteredNodes.filter(d => _findIndex(filteredNodes, ['name', d.name]) === -1);
    }

    this.filteredNodes = filteredNodes;

    this.filteredNodes.forEach(d => {
      if (!d.status) {
        d.status = 'new';
      }
    });
  }

  setData(data, separate) {
    this.data = data;
    this.separate = separate.map(d => {
      return {
        category: d.CATEGORY,
        name: d.COMPANY,
        region: (d.CONTINENT === 'CN' || d.CONTINENT === 'JP') ? 'AS' : d.CONTINENT,
        '2025': d.NUMBER,
      };
    });

    const coefficient = data.length / 900 > 1 ? 900 / data.length : 1;
    this.radius.range([minRadius * coefficient, maxRadius * coefficient]);
  }

  setFont(fontFamily) {
    this.fontFamily = fontFamily;
  }
  getWidth() { return this.width; }
  getHeight() { return this.height; }
}
